import type { ChatbotMode } from './api/types';
import type { StorageKind } from './state/sessionStore';

export type WidgetLayout = 'panel' | 'floating';

export interface AskJuminoTheme {
  accent?: string;
  accentHover?: string;
  gradient?: string;
  radius?: string;
  fontFamily?: string;
  zIndex?: string;
}

export interface AskJuminoStrings {
  title?: string;
  placeholder?: string;
  empty?: string;
}

export interface AskJuminoConfig {
  /** KB service origin. Portal dev: '/kbService'. A deployed origin later for both hosts. */
  baseUrl: string;
  /** 'support' cites sources and requires a signed-in portal user (the tenant is
   * derived from the token by the backend); 'technical-sales' is anonymous. */
  mode: ChatbotMode;
  /** 'panel' = fill the host container (portal drawer); 'floating' = bubble + slide-up panel. */
  layout: WidgetLayout;
  /** Called before every request. The portal returns x-auth-token / X-Tenant-Uid. */
  getHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
  /** Storage namespace for the saved conversation. Default 'ask-jumino-conversation'. */
  storageKey?: string;
  /** How long a saved conversation lives: 'local' outlives the tab, 'session' is
   * wiped when it closes. Leave unset to take the derived default — see
   * defaultStorageKind(), which is what both hosts actually rely on. */
  storage?: StorageKind;
  /** Show a Continue/Start New prompt when a stored conversation exists, instead
   * of resuming it silently. Default: true for support, false for technical-sales
   * — technical-sales still resumes automatically, it just doesn't ask first. */
  resumePrompt?: boolean;
  theme?: AskJuminoTheme;
  strings?: AskJuminoStrings;
}

/**
 * Where a conversation is stored when the host doesn't say.
 *
 * Anonymous ('technical-sales') and site-embedded ('floating') widgets get
 * per-tab storage: a public visitor who comes back tomorrow starts fresh
 * rather than resuming a thread they've forgotten writing. Only the
 * signed-in shape — panel + support, i.e. the portal drawer — persists past
 * the tab, where a user does expect their thread to still be there.
 *
 * An explicit `storage` (config property or `storage` attribute) always wins.
 */
export function defaultStorageKind(
  config: Pick<Partial<AskJuminoConfig>, 'mode' | 'layout'>,
): StorageKind {
  // Equivalent to (floating || technical-sales) → 'session' for any complete
  // config, but written as the inverse so one missing field errs toward the
  // shorter-lived store rather than the longer one.
  return config.layout === 'panel' && config.mode === 'support' ? 'local' : 'session';
}

export const DEFAULT_CONFIG: Partial<AskJuminoConfig> = {
  mode: 'technical-sales',
  layout: 'panel',
  storageKey: 'ask-jumino-conversation',
};
